import React from "react";
import { getItemPropertyValue, Item } from "../../data-types";
import { VpdList } from "../vpd-list";
import { Button, Typography } from "antd";
import { CheckCircleTwoTone, CopyOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Colors } from "../colors";
import { Parser } from "html-to-react";

const parser = Parser();

export type LibraryListProps<T extends Item> = {
  itemKeyProperty: string;
  itemTextProperty: string;
  loading: boolean;
  items: T[];
  suggestedItems?: T[];
  selectedItems: T[];
  copyLibraryItem: (item: T) => void;
  labels?: Record<string, string>;
  contentSuffix?: (item: T) => React.ReactNode;
  isSelectedFunction?: (item: T, selectedItems?: T[]) => boolean;
};

export const LibraryList = <T extends Item>({
  itemKeyProperty,
  itemTextProperty,
  loading,
  items,
  suggestedItems,
  selectedItems,
  copyLibraryItem,
  labels,
  contentSuffix,
  isSelectedFunction,
}: LibraryListProps<T>) => {
  return (
    <>
      {suggestedItems && suggestedItems.length > 0 && (
        <>
          <div style={{ marginBottom: "10px" }}>
            <h3>Suggested:</h3>
          </div>
          <VpdList
            itemKeyProperty={itemKeyProperty}
            itemTextProperty={itemTextProperty}
            loading={loading}
            split={false}
            items={suggestedItems}
            renderItem={(item) =>
              renderItem(
                item,
                itemKeyProperty,
                itemTextProperty,
                selectedItems,
                copyLibraryItem,
                contentSuffix,
                !items.includes(item),
                isSelectedFunction
              )
            }
            listStyle="primary"
            labels={{
              emptyText: "No suggested assumptions available.",
              loadingText: "Loading suggested assumptions...",
              ...labels,
            }}
          />
          <hr style={{ margin: "20px 0" }} />
        </>
      )}
      <VpdList
        itemKeyProperty={itemKeyProperty}
        itemTextProperty={itemTextProperty}
        loading={loading}
        split={false}
        items={items.filter((i) => !suggestedItems?.includes(i))}
        renderItem={(item) =>
          renderItem(
            item,
            itemKeyProperty,
            itemTextProperty,
            selectedItems,
            copyLibraryItem,
            contentSuffix,
            false,
            isSelectedFunction
          )
        }
        listStyle="primary"
        labels={{
          emptyText: "No assumptions available.",
          loadingText: "Loading assumptions...",
          ...labels,
        }}
      />
    </>
  );
};

function renderItem<T extends Item>(
  item: T,
  itemKeyProperty: string,
  itemTextProperty: string,
  selectedItems: T[],
  copyLibraryItem: (item: T) => void,
  contentSuffix?: (item: T) => React.ReactNode,
  aiGenerated?: boolean,
  isSelectedFunction?: (item: T, selectedItems?: T[]) => boolean
) {
  const key = getItemPropertyValue(item, itemKeyProperty);
  const text = getItemPropertyValue(item, itemTextProperty);
  const isSelected = isSelectedFunction
    ? isSelectedFunction(item, selectedItems)
    : selectedItems.some((i) => getItemPropertyValue(i, itemTextProperty) === text);
  return (
    <div>
      <VpdList.Item
        key={key}
        content={<Typography.Text>{parser.parse(text)}</Typography.Text>}
        contentPrefix={
          isSelected ? (
            <CheckCircleTwoTone twoToneColor={Colors.Success} />
          ) : aiGenerated ? (
            <ThunderboltOutlined />
          ) : null
        }
        contentSuffix={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
            }}
          >
            {contentSuffix ? (
              contentSuffix(item)
            ) : (
              <Button
                icon={<CopyOutlined />}
                shape="circle"
                size="small"
                type="primary"
                onClick={() => copyLibraryItem(item)}
              />
            )}
          </div>
        }
      />
    </div>
  );
}
